import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  getProductInfo,
  Variation,
} from '@/checkout-instore-legacy/utils/ProductUtils'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { getDefaultSellerId } from '@/checkout-instore-legacy/seller/utils'

import Bullet from '@/checkout-instore-legacy/components/Commons/Bullet'

import { ProductSellerName } from './ProductSellerName'
import { csx, cx } from '@vtex/admin-ui'

interface ProductNameProps {
  item?: Item
  showSellerInfo?: boolean
  showInfo?: boolean
}

const ProductName: FC<ProductNameProps> = ({
  item,
  showSellerInfo = true,
  showInfo = true,
}) => {
  const [refId, setRefId] = useState<string | undefined>('')
  const [name, setName] = useState('')
  const [seller, setSeller] = useState('')
  const [variations, setVariations] = useState<Variation[]>([])
  const [quantity, setQuantity] = useState<number>(1)

  const getProductInfoData = useCallback(async () => {
    if (!item) return
    const productInfo = await getProductInfo(item as Item)
    setRefId(productInfo.refId)
    setSeller(productInfo.seller)
    setName(productInfo.name)
    setVariations(productInfo.variations)
    setQuantity(productInfo.quantity)
  }, [item])

  useEffect(() => {
    getProductInfoData()
  }, [getProductInfoData])

  if (!item) {
    return null
  }

  const displayName = name.replace(item.skuName, '') || name
  return (
    <div
      className="flex flex-column flex-auto w-100"
      id={`productlistitem-${item.index}-name`}
      data-cy="product-summary__name"
    >
      <div
        className={cx(
          csx({
            fontFamily: 'VTEX Trust',
          }),
          't-heading-5-ns t-small-s mb0-m mb1-s truncate mw-100 c-on-base'
        )}
      >
        {displayName}
      </div>
      {showInfo ? (
        <ProductInfo
          variations={variations}
          refId={refId}
          seller={seller}
          showSellerInfo={showSellerInfo}
          quantity={quantity}
        />
      ) : null}
    </div>
  )
}

const ProductInfo: FC<{
  variations: Variation[]
  refId?: string
  showSellerInfo: boolean
  seller: string
  quantity: number
}> = ({ variations, refId, seller, showSellerInfo, quantity }) => {
  const variationLabels = variations.map(({ value }) => ` ${value}`)
  const hasVariations = !isEmpty(variations)

  const hasRef = !isNil(refId)
  const shouldShowSeller = showSellerInfo && getDefaultSellerId() !== seller

  return (
    <div className="w-100 f7-s f6-ns c-muted-2 t-small items-center truncate mt2">
      <div>
        {hasRef ? (
          <div>
            <span>
              {' '}
              <FormattedMessage id="product.refId" values={{ ref: refId }} />
              {hasVariations && (
                <Bullet radius={1} className="bg-c-muted-2 mh3" />
              )}
            </span>
          </div>
        ) : null}

        {hasVariations ? (
          <div className={`truncate pt2 ${showSellerInfo ? '' : 'pb5'}`}>
            <span>{variationLabels.join(', ')}</span>
          </div>
        ) : null}

        <div className="pb2">
          <FormattedMessage
            id="product.quantity"
            values={{ quantity: quantity }}
          />
        </div>

        {shouldShowSeller ? (
          <div className="pt2 pb6">
            <ProductSellerName sellerId={seller} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ProductName
